<script setup lang="ts">
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { APPEALS_CONTEXTS } from '@/modules/onboarding/scenarios/appeals/anchors'
import { ERouteNames } from '@/router/ERouteNames'
import AppealChannelsTab from './AppealChannelsTab.vue'

const APPEALS_SETTINGS_TABS = ['channels'] as const
type TAppealsSettingsTab = typeof APPEALS_SETTINGS_TABS[number]

const route = useRoute()
const router = useRouter()
const permissionsStore = usePermissionsStore()

const canChannels = computed(() => Boolean(permissionsStore.isSuperuser || permissionsStore.modules.appeals?.channel_edit))

// Онбординг заходит сюда из реестра обращений и по «Назад» возвращается туда же
useOnboardingContext({
  names: [APPEALS_CONTEXTS.settings],
  root: () => document.body,
  actions: {
    backToAppeals: async () => { await router.push({ name: ERouteNames.CITIZEN_INQUIRIES }) },
  },
})

function appealsTabFromQuery(): TAppealsSettingsTab {
  const q = route.query.tab
  if (typeof q === 'string' && (APPEALS_SETTINGS_TABS as readonly string[]).includes(q)) {
    return q as TAppealsSettingsTab
  }
  return 'channels'
}

const tab = ref<TAppealsSettingsTab>(appealsTabFromQuery())

onMounted(async () => {
  if (!route.query.tab) {
    await router.replace({ query: { ...route.query, tab: tab.value } }).catch((err: unknown) => {
      if ((err as { name?: string }).name !== 'NavigationDuplicated') {
        console.error(err)
      }
    })
  }
})

watch(tab, async (newTab, oldTab) => {
  if (oldTab && newTab !== oldTab && route.query.tab !== newTab) {
    await router.replace({ query: { ...route.query, tab: newTab } }).catch((err: unknown) => {
      if ((err as { name?: string }).name !== 'NavigationDuplicated') {
        console.error(err)
      }
    })
  }
})

watch(() => route.query.tab, newTabQuery => {
  if (typeof newTabQuery === 'string' && (APPEALS_SETTINGS_TABS as readonly string[]).includes(newTabQuery)) {
    if (tab.value !== newTabQuery) {
      tab.value = newTabQuery as TAppealsSettingsTab
    }
  }
})
</script>

<template>
  <div class="d-flex flex-column ga-4">
    <VTabs
      v-if="canChannels"
      v-model="tab"
      color="primary"
      style="width: max-content; max-width: 100%; flex-shrink: 0;"
    >
      <VTab value="channels">Каналы</VTab>
    </VTabs>

    <div v-if="!canChannels" class="text-medium-emphasis">
      Недостаточно прав для настроек модуля.
    </div>
    <AppealChannelsTab v-else-if="tab === 'channels'" />
  </div>
</template>
